/**
 * Modeldetail (`/personas/show/:id`) — het grootste subscherm van het oude
 * dashboard. Gemeten met scratchpad/layout.py en traffic.py op oud.
 *
 * Vier endpoints voeden dit scherm, alle vier eenmalig (geen polling):
 *   models/personas/{id}                 kern, chatters, model_info, notes
 *   models/personas/{id}/audience        volgers, betalende fans, events
 *   models/personas/{id}/earnings        omzet, per platform/type, top spenders
 *   models/personas/{id}/payout-splits   uitbetaalverdeling per transactietype
 *
 * Blokvolgorde zoals oud: kop → Advanced Settings → Personality → Chatters →
 * Audience → New Followers → Paid Subscribers → Payout Split → Earnings
 * Breakdown → Top Spenders → FC Vault Inspector → Model Info → Chatter
 * Snapshot → Notes → Full Config.
 */
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Avatar, ErrorNote, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { dt, eur, num } from '../lib/format'
import { isBeheer, isVolledigBeheer, useMij } from '../lib/rechten'
import PersonaGids from '../components/PersonaGids'
import EigenVelden, { eigenVeldenUit } from '../components/EigenVelden'
import type { EigenVeld } from '../components/EigenVelden'

/** De AI-persona zoals de server hem geeft (alleen owner/admin). */
interface AiPersona {
  personality: Record<string, unknown>
  communication: Record<string, unknown>
  safety: { hard_boundaries?: string[]; stop_phrases?: string[] }
  feiten: Record<string, unknown>
  vaste_regels: string[]
}

const regels = (v: unknown) => (Array.isArray(v) ? v.map(String).join('\n') : String(v ?? ''))
const lijst = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean)

/** 07-10-2026 (Jordy, punt 5): de AI-persona voor de bot. Alleen owner/admin
 *  zien en bewerken hem; de server weigert anderen (403). De feiten komen
 *  vanzelf uit Model Info; de vaste veiligheidsregels kunnen er niet uit.
 *  AI-antwoorden staan uit: dit verandert pas iets na een workerherstart. */
function AiPersonaPaneel({ personaId, versie }: { personaId: string; versie: number }) {
  const a = useApi<AiPersona>(`/api/admin/models/personas/${personaId}/ai-persona`, [personaId, versie])
  const { run, busy } = useActions()
  const [d, setD] = useState<Record<string, string> | null>(null)
  if (a.loading && !a.data) return <Loading />
  if (a.error) return <ErrorNote error={a.error} />
  const x = a.data
  if (!x) return null
  const vast = new Set(x.vaste_regels)
  const basis: Record<string, string> = {
    description: String(x.personality.description ?? ''),
    energy_level: String(x.personality.energy_level ?? ''),
    flirt_level: String(x.personality.flirt_level ?? ''),
    boundaries: regels(x.personality.boundaries),
    language: String(x.communication.language ?? ''),
    language_notes: String(x.communication.language_notes ?? ''),
    style: String(x.communication.style ?? ''),
    emoji: String(x.communication.emoji ?? ''),
    forms_of_address: String(x.communication.forms_of_address ?? ''),
    hard_boundaries: (x.safety.hard_boundaries ?? []).filter((r) => !vast.has(r)).join('\n'),
  }
  const v = d ?? basis
  const zet = (k: string, w: string) => setD({ ...v, [k]: w })
  const veld = (k: string, label: string, rijen = 1) => (
    <div className="field" key={k}>
      <label>{label}</label>
      <textarea className="inp" rows={rijen} value={v[k]} onChange={(e) => zet(k, e.target.value)} />
    </div>
  )
  const bewaar = () => run({
    label: 'AI persona',
    request: mut.put(`/api/admin/models/personas/${personaId}/ai-persona`, {
      personality: {
        description: v.description, energy_level: v.energy_level || undefined,
        flirt_level: v.flirt_level ? Number(v.flirt_level) : undefined,
        boundaries: lijst(v.boundaries),
      },
      communication: {
        language: v.language, language_notes: v.language_notes, style: v.style,
        emoji: v.emoji, forms_of_address: v.forms_of_address,
      },
      safety: { hard_boundaries: [...x.vaste_regels, ...lijst(v.hard_boundaries)] },
    }),
    onDone: () => { setD(null); a.refetch() },
    successText: 'AI persona saved.',
  })
  return (
    <>
      <div className="note" style={{ marginBottom: 10 }}>
        Owner/admin only. AI replies are off; changes take effect after the next worker restart.
      </div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <b style={{ fontSize: 12.5 }}>Personality</b>
          {veld('description', 'Description', 3)}
          <div className="field">
            <label>Energy level</label>
            <select className="inp" value={v.energy_level} onChange={(e) => zet('energy_level', e.target.value)}>
              <option value="">—</option><option value="low">low</option>
              <option value="medium">medium</option><option value="high">high</option>
            </select>
          </div>
          <div className="field">
            <label>Flirt level (1–10)</label>
            <input className="inp" type="number" min={1} max={10} value={v.flirt_level}
                   onChange={(e) => zet('flirt_level', e.target.value)} />
          </div>
          {veld('boundaries', 'Boundaries (one per line)', 4)}
        </div>
        <div>
          <b style={{ fontSize: 12.5 }}>Communication</b>
          <div className="field">
            <label>Default language</label>
            <select className="inp" value={v.language} onChange={(e) => zet('language', e.target.value)}>
              <option value="">—</option><option value="en">English</option><option value="nl">Dutch</option>
            </select>
          </div>
          {veld('language_notes', 'Language notes', 2)}
          {veld('style', 'Style', 2)}
          {veld('emoji', 'Emoji')}
          {veld('forms_of_address', 'Forms of address')}
        </div>
      </div>
      <b style={{ fontSize: 12.5, display: 'block', marginTop: 10 }}>Safety</b>
      <div className="mut2" style={{ fontSize: 12, margin: '4px 0' }}>
        Fixed rules (always on, cannot be removed):
      </div>
      <ul style={{ margin: '0 0 8px', paddingLeft: 20, fontSize: 12.5 }}>
        {x.vaste_regels.map((r) => <li key={r}>{r}</li>)}
      </ul>
      {veld('hard_boundaries', 'Extra hard boundaries for this model (one per line)', 3)}
      {x.safety.stop_phrases?.length ? (
        <div className="mut2" style={{ fontSize: 12 }}>
          Stop phrases: {x.safety.stop_phrases.length} (from CHATTERS-STOPPEN.md)
        </div>
      ) : null}
      <b style={{ fontSize: 12.5, display: 'block', marginTop: 10 }}>Facts (synced from Model Info)</b>
      <div className="kv">
        {Object.entries(x.feiten).map(([k, w]) => (
          <span key={k} style={{ display: 'contents' }}>
            <span>{k}</span>
            <span>{typeof w === 'object' ? JSON.stringify(w) : String(w)}</span>
          </span>
        ))}
        {Object.keys(x.feiten).length === 0 ? <span className="mut2">No facts yet.</span> : null}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 10 }}>
        <button className="btn primary sm" type="button" disabled={!d || busy === 'AI persona'}
                onClick={bewaar}>
          Save AI persona
        </button>
      </div>
    </>
  )
}

interface ChatterLink {
  assignment_id: number
  chatter_id: number
  chatter_name: string
  is_primary: boolean
}

interface Persona {
  id: number
  slug: string
  name: string | null
  stage_name: string | null
  avatar_url: string | null
  active: boolean
  fancentro_model_id: string | null
  f2f_model_id: string | null
  chatters: ChatterLink[]
  model_info: Record<string, unknown> | null
  notes: string | null
  persona_config: Record<string, unknown> | null
  original_stage_names: Record<string, string> | null
  sibling_persona_ids: number[] | null
  created_at: string | null
  updated_at: string | null
}

interface Audience {
  stage_name: string
  last_fetched_at: string | null
  totals: { followers: number; paid_fans: number }
  per_platform: Record<string, { followers: number; paid_fans: number }>
  /** 07-10-2026: de server geeft per meting `delta` (nieuw sinds de vorige
   *  meting) en `follower_count` (totaal op dat moment). */
  follower_events: {
    recorded_at: string; platform: string; model_slug: string
    delta: number; follower_count: number
  }[]
  paid_subs: Record<string, unknown>[]
  paid_subs_window: Record<string, unknown>[]
  window: { from?: string; to?: string; new_followers?: number } | null
}

interface Earnings {
  days: number
  total_revenue: number
  net_revenue: number
  total_transactions: number
  by_platform: Record<string, { total: number; net: number; count: number }>
  by_type: Record<string, { total: number; net: number; count: number }>
  daily: { date: string; revenue: number; net: number }[]
  top_spenders: Record<string, unknown>[]
}

interface Split {
  id: number
  model_slug: string
  platform: string
  transaction_type: string
  model_pct?: number | null
  agency_pct?: number | null
  creator_pct?: number | null
}

/** De vaste velden van de Model Info-editor, in de volgorde van oud. */
/** De Chatter Snapshot: velden in model_info die een chatter helpen een model
 *  over te nemen. 30-09-2026 (Jordy): zichtbaar én bewerkbaar voor chatters. */
const SNAPSHOT_FIELDS: [string, string][] = [
  ['summary', 'Quick summary'],
  ['recent_topics', 'Recently talked about'],
  ['intent', 'Intent'],
  ['spender_level', 'Spender level'],
  ['dos', 'DO'],
  ['donts', "DON'T"],
  ['hook', 'Hook'],
  ['next_move', 'Next move'],
]

const INFO_FIELDS: [string, string][] = [
  ['name', 'Name / Username (real)'],
  ['age', 'Age'],
  ['dos', "Do's"],
  ['donts', "Don'ts"],
  ['location', 'Location / Country'],
  // 19/20-10-2026 (Jordy): interne velden; nooit naar een fan (server sluit ze uit).
  ['plaats', 'City (internal)'],
  ['land', 'Country (internal)'],
  ['timezone', 'Timezone'],
  ['work', 'Work / Studies'],
  ['relationship', 'Relationship status'],
  ['hobbies', 'Hobbies / Lifestyle'],
  ['preferences', 'Preferences (general)'],
  ['interests', 'Personal interests'],
  ['enjoys_in_chats', 'What he enjoys in chats'],
  ['important_events', 'Important events'],
  ['memory_points', 'Memory points'],
]

const RANGES = [
  { value: '1', label: 'Today' }, { value: '7', label: 'Week' },
  { value: '30', label: 'Month' }, { value: '90', label: 'Quarter' },
  { value: '365', label: 'Year' }, { value: 'all', label: 'All' },
]
const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]

/** "All" bestaat niet als getal: de server neemt `days` 1–3650 (422 bij
 *  "all"). All = de maximale periode van tien jaar. */
function dagenVoorServer(d: string): string {
  return d === 'all' ? '3650' : d
}

export default function ModelShow() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { run, busy } = useActions()
  const [days, setDays] = useState('30')
  const [platform, setPlatform] = useState('all')
  const [info, setInfo] = useState<Record<string, string> | null>(null)
  const [notes, setNotes] = useState<string | null>(null)
  const [eigen, setEigen] = useState<EigenVeld[] | null>(null)
  const [aiVersie, setAiVersie] = useState(0)

  /* 30-09-2026 (Jordy): een chatter ziet op deze pagina alleen Model Info, de
     Chatter Snapshot en Notes. Volgers, split, verdiensten, vault, personality
     en de ruwe config zijn voor beheer — de server geeft ze een chatter ook
     niet meer; hier worden ze niet eens opgevraagd. */
  const mij = useMij()
  const beheer = isBeheer(mij.data)
  const geld = isVolledigBeheer(mij.data)
  /** 07-10-2026 (punt 5): AI-persona, ruwe config en gids bewerken. */
  const eigenaar = ['owner', 'admin'].includes(String(mij.data?.role ?? '').toLowerCase())
  const p = useApi<Persona>(`/api/admin/models/personas/${id}`, [id])
  const aud = useApi<Audience>(
    beheer ? `/api/admin/models/personas/${id}/audience?days=${dagenVoorServer(days)}` : null,
    [id, beheer, days])
  const earn = useApi<Earnings>(
    geld ? `/api/admin/models/personas/${id}/earnings?days=${dagenVoorServer(days)}${platform === 'all' ? '' : `&platform=${platform}`}` : null,
    [id, days, platform, geld],
  )
  const splits = useApi<{ splits: Split[] }>(
    geld ? `/api/admin/models/personas/${id}/payout-splits` : null, [id, geld])

  const m = p.data
  /** De editor werkt op een kopie; pas bij Save gaat het naar de server. */
  const infoDraft = useMemo(() => {
    if (info) return info
    const src = (m?.model_info ?? {}) as Record<string, unknown>
    const out: Record<string, string> = {}
    for (const [k] of INFO_FIELDS) out[k] = String(src[k] ?? '')
    for (const [k] of SNAPSHOT_FIELDS) out[k] = String(src[k] ?? '')
    for (const [k, v] of Object.entries(src)) {
      if (!k.startsWith('_') && !(k in out)) out[k] = String(v ?? '')
    }
    return out
  }, [info, m])
  const eigenVelden = eigen ?? eigenVeldenUit(m?.model_info as Record<string, unknown> | null)
  const infoDirty = info !== null || eigen !== null
  const notesDirty = notes !== null

  if (p.loading && !p.data) return <Loading />
  if (p.error) return <ErrorNote error={p.error} />
  if (!m) return <ErrorNote error="Model not found." />

  const name = m.stage_name || m.slug
  const custom = Object.keys(infoDraft).filter(
    (k) => !INFO_FIELDS.some(([f]) => f === k) && !SNAPSHOT_FIELDS.some(([f]) => f === k))
  /** Model Info, Snapshot, eigen velden en Notes in één opslag, via de smalle
   *  profielroute (ook voor een chatter, voor zijn eigen modellen). De server
   *  voegt samen en werkt de feiten in de AI-persona bij; een leeftijd onder
   *  18 weigert hij met een melding. */
  const bewaarInfo = () => run({
    label: 'Model info',
    request: mut.patch(`/api/admin/models/personas/${id}/profile`, {
      ...(infoDirty ? {
        model_info: {
          ...infoDraft,
          _custom_fields: eigenVelden.filter((v) => v.label.trim() || v.value.trim()),
        },
      } : {}),
      ...(notesDirty ? { notes } : {}),
    }),
    onDone: () => { setInfo(null); setEigen(null); setNotes(null); p.refetch(); setAiVersie((n) => n + 1) },
    successText: 'Saved.',
  })

  const setField = (k: string, v: string) =>
    setInfo({ ...infoDraft, [k]: v })

  const splitRows = (plat: string) =>
    (splits.data?.splits ?? []).filter((s) => s.platform === plat)

  return (
    <>
      <div className="filters">
        <button className="btn ghost sm" type="button" onClick={() => navigate('/personas')}>
          Models
        </button>
        {beheer ? (
          <button className="btn ghost sm" type="button"
                  onClick={() => navigate(`/personas/edit/${id}`)}>
            Edit
          </button>
        ) : null}
        <button className="btn ghost sm" type="button"
                disabled={busy === 'Refresh'}
                onClick={() => run({
                  label: 'Refresh',
                  request: async () => {
                    p.refetch()
                    if (beheer) aud.refetch()
                    if (geld) earn.refetch()
                    return null
                  },
                  successText: 'Refreshed.',
                })}>
          Refresh
        </button>
      </div>

      {/* ---- kop ---- */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="body">
          <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Avatar name={name} src={m.avatar_url} size={44} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 17 }}>{name}</div>
              <div className="mut2" style={{ fontSize: 12 }}>
                Persona: {m.slug} · Active: {m.active ? 'Yes' : 'No'}
                {m.fancentro_model_id ? ` · FanCentro ID ${m.fancentro_model_id}` : ''}
                {m.f2f_model_id ? ` · f2f ID ${m.f2f_model_id}` : ''}
              </div>
            </div>
            <div className="spacer" style={{ flex: 1 }} />
            {m.fancentro_model_id ? (
              <button className="btn ghost sm" type="button"
                      onClick={() => window.open(
                        `https://fancentro.com/${m.slug.replace(/^fc_/, '')}`, '_blank')}>
                Open in FanCentro
              </button>
            ) : null}
            {m.f2f_model_id ? (
              <button className="btn ghost sm" type="button"
                      onClick={() => window.open(
                        `https://f2f.com/agency/creators/${m.f2f_model_id}/`, '_blank')}>
                Open in F2F
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* 07-10-2026 (Jordy, punt 5): de leesbare persona-gids. */}
      <Panel inklap="model.persona" title="Persona"
             desc="Guide for chatters: background, tone, language, hobbies, boundaries, fixed answers.">
        <PersonaGids personaId={String(id)} />
      </Panel>

      {eigenaar ? (
        <Panel inklap="model.ai-persona" title="AI Persona"
               desc="Structured version for the bot. Owner/admin only.">
          <AiPersonaPaneel personaId={String(id)} versie={aiVersie} />
        </Panel>
      ) : null}

      {beheer ? (<>
      {eigenaar ? (
      <Panel title="Advanced Settings"
             desc="Everything that does not fit the profile lives in persona_config.">
        <div className="kv">
          {Object.entries(m.persona_config ?? {}).slice(0, 12).map(([k, v]) => (
            <span key={k} style={{ display: 'contents' }}>
              <span>{k}</span>
              <span className="mono">{String(typeof v === 'object' ? JSON.stringify(v) : v)}</span>
            </span>
          ))}
          {Object.keys(m.persona_config ?? {}).length === 0
            ? <span className="mut2">No advanced settings.</span> : null}
        </div>
      </Panel>
      ) : null}

      <Panel title="Chatters" desc={`${m.chatters.length} assigned`}>
        <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
          {m.chatters.length === 0
            ? <span className="mut2">No chatters assigned.</span>
            : m.chatters.map((c) => (
                <Tag key={c.assignment_id} kind={c.is_primary ? 'acc' : 'mut'}>
                  {c.chatter_name}
                </Tag>
              ))}
        </div>
      </Panel>

      {/* ---- Audience ---- */}
      <Panel
        title="Audience"
        desc={aud.data?.last_fetched_at
          ? `snapshot · ${dt(aud.data.last_fetched_at)}` : undefined}
        right={<Seg options={RANGES} value={days} onChange={setDays} />}
      >
        <div className="grid" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
          <div className="card kpi">
            <div className="lbl">Total Followers</div>
            <div className="val">{num(aud.data?.totals?.followers)}</div>
            <div className="meta">
              FC {num(aud.data?.per_platform?.fancentro?.followers)} ·
              F2F {num(aud.data?.per_platform?.f2f?.followers)}
            </div>
          </div>
          <div className="card kpi">
            <div className="lbl">Paid Subscribers (active snapshot)</div>
            <div className="val">{num(aud.data?.totals?.paid_fans)}</div>
            <div className="meta">
              FC {num(aud.data?.per_platform?.fancentro?.paid_fans)} ·
              F2F {num(aud.data?.per_platform?.f2f?.paid_fans)}
            </div>
          </div>
          <div className="card kpi">
            <div className="lbl">Subs in window</div>
            <div className="val">{num(aud.data?.paid_subs_window?.length)}</div>
            <div className="meta">in the selected window</div>
          </div>
        </div>

        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
          {(['f2f', 'fancentro'] as const).map((plat) => (
            <div className="card" key={plat}>
              <div className="between" style={{ marginBottom: 8 }}>
                <b style={{ fontSize: 12 }}>Audience {plat === 'f2f' ? 'F2F' : 'FC'}</b>
                <Plat value={plat} />
              </div>
              <div className="kv">
                <span>Followers</span>
                <span className="mono">{num(aud.data?.per_platform?.[plat]?.followers)}</span>
                <span>Paid fans</span>
                <span className="mono">{num(aud.data?.per_platform?.[plat]?.paid_fans)}</span>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel
        title="New Followers"
        desc="Each measurement is a snapshot; Running Total is the total at that moment."
        flush
      >
        {/* Oud houdt dit blok kort met een eigen scrollbalk; zonder die grens
            wordt de pagina meters lang. */}
        <div className="listscroll">
        <Table
          cols={[
            { key: 'w', label: 'When' }, { key: 'p', label: 'Platform' },
            { key: 'n', label: 'New Followers', num: true },
            { key: 'r', label: 'Running Total', num: true },
          ]}
          emptyText="No follower measurements in this window."
        >
          {(aud.data?.follower_events ?? []).map((e, i) => (
            <tr key={i}>
              <td className="mut2" style={{ fontSize: 12 }}>{dt(e.recorded_at)}</td>
              <td><Plat value={e.platform} /></td>
              <td className="num mono">{num(e.delta)}</td>
              <td className="num mono">{num(e.follower_count)}</td>
            </tr>
          ))}
        </Table>
        </div>
      </Panel>

      <Panel title={`Paid Subscribers (${aud.data?.paid_subs_window?.length ?? 0} in window)`} flush>
        <div className="listscroll">
        <Table
          cols={[
            { key: 'f', label: 'Fan' }, { key: 'p', label: 'Platform' },
            { key: 'pl', label: 'Plan' }, { key: 'n', label: 'Net', num: true },
            { key: 's', label: 'Started' },
          ]}
          emptyText="No paid subscribers in this window."
        >
          {(aud.data?.paid_subs_window ?? []).map((s, i) => (
            <tr key={i}>
              <td>{String(s.fan_name ?? '—')}</td>
              <td><Plat value={s.platform} /></td>
              <td className="mut">{s.plan_months ? `${s.plan_months}m` : '—'}</td>
              <td className="num mono">{eur(s.net_revenue)}</td>
              <td className="mut2" style={{ fontSize: 12 }}>{dt(s.started_at)}</td>
            </tr>
          ))}
        </Table>
        </div>
      </Panel>

      {/* 05-10-2026 (Jordy): uitbetaalverdeling, omzet en de top spenders uit de
          omzetroute zijn Earnings-gegevens; een manager krijgt ze niet (server 403). */}
      {geld ? (<>
      {/* ---- Payout Split ---- */}
      <Panel
        title="Payout Split"
        desc="Per transaction type: the model's share and the agency's share. The worker reads the splits from the platforms automatically."
      >
        {/* 07-10-2026: de knop "Refresh from platforms" stuurde de uitbetalings-
            scraper (dashboard/payouts/refresh) aan, niet de split-scraper. De
            juiste route (personas/{id}/payout-splits/refresh) werkt alleen in de
            worker (503 op het web) en slaat de twijfelcontrole over; daarom geen
            knop meer. */}
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          {(['f2f', 'fancentro'] as const).map((plat) => (
            <div key={plat}>
              <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>
                {plat === 'f2f' ? 'F2F' : 'FanCentro'}
              </div>
              <Table
                cols={[
                  { key: 't', label: 'Transaction Type' },
                  { key: 'm', label: 'Model %', num: true },
                  { key: 'a', label: 'Agency %', num: true },
                ]}
                emptyText="No splits configured."
              >
                {splitRows(plat).map((s) => (
                  <tr key={s.id}>
                    <td className="mut">{s.transaction_type}</td>
                    {/* 07-10-2026: `agency_pct` is het MODELdeel en `creator_pct`
                        het agency-deel (zie split_uit_rij in
                        src/services/modelaandeel.py); stond omgedraaid. */}
                    <td className="num mono">{s.agency_pct == null ? '—' : `${num(s.agency_pct)}%`}</td>
                    <td className="num mono">{s.creator_pct == null ? '—' : `${num(s.creator_pct)}%`}</td>
                  </tr>
                ))}
              </Table>
            </div>
          ))}
        </div>
      </Panel>

      {/* ---- Earnings ---- */}
      <Panel
        title="Earnings Breakdown"
        right={
          <>
            <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
            <Seg options={RANGES} value={days} onChange={setDays} />
          </>
        }
      >
        <div className="grid" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
          <div className="card kpi">
            <div className="lbl">Gross</div>
            <div className="val">{eur(earn.data?.total_revenue)}</div>
          </div>
          <div className="card kpi">
            <div className="lbl">Net</div>
            <div className="val">{eur(earn.data?.net_revenue)}</div>
          </div>
          <div className="card kpi">
            <div className="lbl">Transactions</div>
            <div className="val">{num(earn.data?.total_transactions)}</div>
          </div>
        </div>
        {(earn.data?.total_transactions ?? 0) === 0 ? (
          <div className="empty" style={{ marginTop: 12 }}>No transactions in this window</div>
        ) : (
          <Table
            cols={[
              { key: 't', label: 'Type' }, { key: 'g', label: 'Gross', num: true },
              { key: 'n', label: 'Net', num: true }, { key: 'c', label: 'Count', num: true },
            ]}
          >
            {Object.entries(earn.data?.by_type ?? {}).map(([k, v]) => (
              <tr key={k}>
                <td className="mut">{k}</td>
                <td className="num mono">{eur(v.total)}</td>
                <td className="num mono">{eur(v.net)}</td>
                <td className="num mono">{num(v.count)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel title="Top Spenders (this model)" flush>
        <div className="listscroll">
        <Table
          cols={[
            { key: 'f', label: 'Fan' }, { key: 'g', label: 'Gross', num: true },
            { key: 'n', label: 'Net', num: true }, { key: 'c', label: 'Purchases', num: true },
          ]}
          emptyText="No spenders in this window."
        >
          {/* 07-10-2026: de server geeft fan_username, gross, net en count. */}
          {(earn.data?.top_spenders ?? []).map((s, i) => (
            <tr key={i}>
              <td>{String(s.fan_username ?? '—')}</td>
              <td className="num mono">{eur(s.gross as number)}</td>
              <td className="num mono">{eur(s.net as number)}</td>
              <td className="num mono">{num(s.count as number)}</td>
            </tr>
          ))}
        </Table>
        </div>
      </Panel>
      </>) : null}

      <Panel
        title="FC Vault Inspector"
        desc="Shows what is currently in this model's FanCentro vault."
      >
        {/* 07-10-2026: de knop deed POST posting/vault-inspect/{id}, een route die
            niet bestaat. De echte (GET posting/fc-vault-inspect/{id}) leest de
            vault via de posting-bot, en die draait niet in het webproces
            (WEB_ONLY): daar geeft hij altijd 503. */}
        <div className="note">
          Not available on the web dashboard: the vault can only be read by the
          posting worker.
        </div>
      </Panel>

      </>) : null}

      {/* ---- Model Info-editor ---- */}
      <Panel
        title="Model Info"
        desc={infoDirty ? 'Unsaved changes' : undefined}
        right={
          <>
            <button
              className="btn primary sm"
              type="button"
              disabled={(!infoDirty && !notesDirty) || busy === 'Model info'}
              onClick={bewaarInfo}
            >
              Save
            </button>
          </>
        }
      >
        {[...INFO_FIELDS, ...custom.map((k) => [k, k] as [string, string])].map(([k, label]) => (
          <div className="field" key={k}>
            <label>{label}</label>
            <textarea
              className="inp"
              rows={String(infoDraft[k] ?? '').length > 60 ? 2 : 1}
              value={infoDraft[k] ?? ''}
              onChange={(e) => setField(k, e.target.value)}
            />
          </div>
        ))}
        <div className="field">
          <label>Custom fields</label>
          <EigenVelden velden={eigenVelden} onChange={setEigen} />
        </div>
      </Panel>

      <Panel title="Chatter Snapshot"
             desc="What a chatter should know before taking over this model."
             right={
               <button className="btn primary sm" type="button"
                       disabled={(!infoDirty && !notesDirty) || busy === 'Model info'}
                       onClick={bewaarInfo}>
                 Save
               </button>
             }>
        {SNAPSHOT_FIELDS.map(([k, label]) => (
          <div className="field" key={k}>
            <label>{label}</label>
            <textarea
              className="inp"
              rows={String(infoDraft[k] ?? '').length > 60 ? 2 : 1}
              value={infoDraft[k] ?? ''}
              onChange={(e) => setField(k, e.target.value)}
            />
          </div>
        ))}
      </Panel>

      <Panel
        title="Notes"
        right={
          <button
            className="btn primary sm"
            type="button"
            disabled={(!infoDirty && !notesDirty) || busy === 'Model info'}
            onClick={bewaarInfo}
          >
            Save
          </button>
        }
      >
        <textarea
          className="inp"
          rows={4}
          value={notes ?? m.notes ?? ''}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Panel>

      {eigenaar ? (
        <Panel title="Full Config" desc="The raw persona_config, as in the old dashboard.">
          <pre className="jsonbox">{JSON.stringify(m.persona_config ?? {}, null, 2)}</pre>
        </Panel>
      ) : null}
    </>
  )
}
